/* eslint-disable @typescript-eslint/indent */
import CTNButton from '@ctn/ui/components/ctn-button';
import CTNCalendar from '@ctn/ui/components/ctn-calendar';
import CTNInput from '@ctn/ui/components/ctn-input';
import CTNMessage from '@ctn/ui/components/ctn-message';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { addDays, addYears } from 'date-fns';
import { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import DocumentGroup from '@/components/DocumentGroup';
import DocumentosListado from '@/components/DocumentosListado';
import ModalAddDocumento from '@/components/ModalAddDocumento';
import { useGlobalContext } from '@/context/global.context';
import { useSolicitudContext } from '@/context/solicitud.context';

import { MESSAGE_PREVIEW_FILE } from '@/constants/file.constants';
import { commonCalendar } from '@/helpers/calendar';
import { flattenGroupDocumentos, getAllEntitiesAdicionals, getAllEntitiesRequireds } from '@/helpers/formSolicitudes';
import { notificationsConfig } from '@/helpers/notifications';
import type { FormValues, IDocumentoForm } from '@/interfaces/model/don.interface';
import { findID } from '@/utils/id';

import TextIcon from '../../../components/TextIcon';

const SolicitudDocumentacionSection = () => {
	const { id } = useParams();
	const {
		maestrosTramite: { tipoDocumento, configuracionTramite },
	} = useGlobalContext();
	const [modalDocumentos, setModalDocumentos] = useState<boolean>(false);
	const { openNotification, closeNotification } = useNotification();
	const { requerirDocumentacion } = useSolicitudContext();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const form =
		useFormContext<
			Pick<FormValues, 'documentosRequeridos' | 'documentosAdicionales' | 'numeroTramite' | 'fechaVencimiento'>
		>();

	const {
		control,
		watch,
		formState: { errors },
	} = form;

	const { append: appendAdicional, remove: removeAdicional } = useFieldArray({
		control,
		name: 'documentosAdicionales',
	});

	const controlArray = useFieldArray({
		control,
		name: 'documentosRequeridos',
	});

	const documentosRequeridos = watch('documentosRequeridos') ?? [];
	const documentosAdicionales = watch('documentosAdicionales') ?? [];
	const hasFechaVencimiento = configuracionTramite?.permiteVencimiento;
	const allDocuments = [...flattenGroupDocumentos(documentosRequeridos), ...documentosAdicionales].filter(Boolean);
	const allEntitiesRequired = getAllEntitiesRequireds(tipoDocumento, true);

	const confirmDeleteDoc = (data: IDocumentoForm) => {
		const index = findID({ list: documentosAdicionales, idReact: data.idReact, id: data.id });

		const modalSuccesEliminarDocument = () => {
			openNotification(
				notificationTemplate.successMessage(
					'Documento eliminado',
					'Se ha eliminado correctamente el documento.',
				),
			);
		};

		openNotification(
			notificationTemplate.removeItem({
				title: 'Eliminar documento',
				content: '¿Estás seguro de eliminar el documento seleccionado?',
				onAccept() {
					setTimeout(() => {
						modalSuccesEliminarDocument();
					}, 300);
					removeAdicional(index);
				},
			}),
		);
	};

	const onSaveDocumentoAdicional = (doc: IDocumentoForm) => {
		appendAdicional(doc);
	};

	return (
		<>
			<form className="u-flow">
				{allEntitiesRequired.length > 0 && requerirDocumentacion && (
					<CTNMessage
						content={
							<div>
								<p>
									Recuerda que para poder formalizar la solicitud es obligatorio adjuntar la siguiente
									documentación requerida:
								</p>
								<ul>
									{allEntitiesRequired.map(({ descripcion, id, minimoObligatorio }) => (
										<li key={id}>
											<p>
												· {descripcion}{' '}
												{`(${minimoObligatorio} ${minimoObligatorio === 1 ? 'Documento' : 'Documentos'})`}
												.
											</p>
										</li>
									))}
								</ul>
							</div>
						}
						icon="warning"
						status="warning"
						mode="outline"
						closable={false}
					/>
				)}
				<fieldset>
					<legend className="c-pnp-heading-h4">Datos de la solicitud</legend>
					<div className="[ o-grid ] [ u-mt-16 ]">
						<div className="o-grid__row">
							<CTNInput
								required
								type="text"
								classNameField="o-grid__col-12 o-grid__col-3@portrait"
								name="numeroTramite"
								label="Número de solicitud"
								control={control}
								maxLength={32}
								error={errors.numeroTramite?.message}
							/>
							{hasFechaVencimiento && (
								<CTNCalendar
									{...commonCalendar}
									required
									monthsShown={1}
									classNameField="o-grid__col-12 o-grid__col-2@portrait"
									name="fechaVencimiento"
									label="Vigencia"
									todayButton=""
									appendTo={undefined}
									minDate={addDays(new Date(), 1)}
									maxDate={addYears(new Date(), 2)}
									control={control}
									error={errors.fechaVencimiento?.message}
								/>
							)}
						</div>
					</div>
				</fieldset>
				{controlArray.fields.length > 0 && (
					<fieldset>
						<TextIcon
							className="c-pnp-heading-h4"
							tag="legend"
							description="Documentación requerida"
							iconRight={{
								icon: 'info',
								color: 'blue-50',
								tooltip: { content: MESSAGE_PREVIEW_FILE, maxWidth: 432, place: 'top' },
							}}
						/>
						{!requerirDocumentacion && (
							<CTNMessage
								content={
									<div>
										<p>A continuación te mostramos la documentación requerida para esta escritura. Si no dispones de la documentación en este momento, podrás enviar la solicitud sin necesidad de adjuntarla.</p>
									</div>
								}
								icon="info"
								status="info"
								mode="outline"
								closable={false}
							/>
						)}
						{controlArray.fields.map((field, index) => (
							<DocumentGroup
								key={field.id}
								name={`documentosRequeridos.${index}`}
								idSolicitud={Number(id)}
								allDocuments={allDocuments}
							/>
						))}
					</fieldset>
				)}
				<section className="u-mt-32">
					<header className="o-splitter o-splitter--responsive">
						<TextIcon
							className="c-pnp-heading-h4"
							tag="h4"
							description="Documentación adicional"
							iconRight={{
								icon: 'info',
								color: 'blue-50',
								tooltip: { content: MESSAGE_PREVIEW_FILE, maxWidth: 432, place: 'top' },
							}}
						/>
						<CTNButton
							mode="secondary"
							label="Añadir documento adicional"
							variant="outline"
							minWidth="140"
							size="size-40"
							onClick={() => setModalDocumentos(true)}
						/>
					</header>
					<div className="u-mt-24 u-mt-16@portrait">
						<DocumentosListado
							documentos={documentosAdicionales}
							noData="Aún no has añadido ningún documento adicional"
							idSolicitud={Number(id as string)}
							confirmDeleteDoc={confirmDeleteDoc}
						/>
					</div>
				</section>
			</form>
			<ModalAddDocumento
				title="Documentos adicionales"
				isOpen={modalDocumentos}
				setIsOpen={setModalDocumentos}
				allDocuments={allDocuments}
				availablesDocumentos={getAllEntitiesAdicionals(tipoDocumento, true)}
				onSave={onSaveDocumentoAdicional}
			/>
		</>
	);
};

export default SolicitudDocumentacionSection;
